import { ThingEventSnapshot } from '@eagle/core-data-types';
import { Alert } from '@ethings/ui-toolkit';
import { DateTime } from 'luxon';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { evaluate } from '../../evaluator';
import { MiddleSpinner } from '../middle-spinner';

interface EventFilter {
  feature: string;
  featureTypeId?: string;
  eventTypeId?: string;
}

interface Props {
  template: unknown;
  thingId: string;
  filters?: EventFilter[];
  startDate?: Date;
  endDate?: Date;
  sort?: string;
  errorComponent?: FC;
  loadingComponent?: FC;
}

const LoadingDisplay: FC = () => {
  return <MiddleSpinner />;
};

const ErrorDisplay: FC<{ thingId: string }> = ({ thingId }) => {
  const { t } = useTranslation();
  return <Alert severity='error' >{t('common:component.events.hint.error', { display: thingId })}</Alert>;
};

const DEFAULT_DATE_START = DateTime.now().minus({ days: 30 }).toJSDate();
const LIMIT = 500;

export const ThingEventsDisplay: FC<Props> = ({ thingId, template, filters, startDate, endDate, loadingComponent: LoadingComponent = LoadingDisplay, errorComponent: ErrorComponent = ErrorDisplay, sort = '-occurred' }) => {
  const { restClient } = useAuthenticated();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<ThingEventSnapshot[] | null>();
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let isCancelled = false;
    const dateRangeStart = startDate || DEFAULT_DATE_START;
    const dateRangeFinish = endDate;
    const eventFilters = filters ? { '$or': filters } : undefined;

    restClient.thingEvent.getWithThing(thingId, { dateRangeStart, dateRangeFinish, limit: LIMIT, sort, filter: eventFilters })
      .then((data) => {
        if (!isCancelled) {
          setData(data.items);
        }
      }).catch((err) => {
        if (!isCancelled) {
          setData(null);
          setError(err instanceof Error ? err : new Error('Failed to fetch event data'));
        }
      }).finally(() => {
        setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [startDate, endDate, sort, thingId, filters, restClient]);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorComponent thingId={thingId} />;
  }

  return <>{evaluate(template, { items: data })}</>;
};
